import React, {useEffect, useRef, useState} from 'react';
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from '@headlessui/react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {playerKeys} from '@/features/players/queries';
import {
  WyscoutClubRosterImportResult,
  WyscoutClubRosterPlayer,
  WyscoutClubRosterPreview,
  wyscoutClubRosterAPI,
} from '@/features/players/wyscoutClubRosterApi';
import {WyscoutImportMonths} from '@/features/players/wyscoutImportApi';
import {getV2Error} from '@/services/api/v2';
import LoadingStatus from '@/components/ui/LoadingStatus';

interface Props {
  open: boolean;
  workspaceId: string;
  playerId: string;
  onClose: () => void;
  onImported: (result: WyscoutClubRosterImportResult, context: {workspaceId: string; playerId: string; existingPlayerIds: string[]}) => void;
}

interface ImportVariables {
  workspaceId: string;
  playerId: string;
  teamId: number;
  playerIds: number[];
  months: WyscoutImportMonths;
  existingPlayerIds: string[];
  token: object;
}

const formatBirthDate = (value: string): string => {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC'}).format(date);
};

const stateLabel = (player: WyscoutClubRosterPlayer): string => {
  if (player.linkState === 'new') return 'New scouting profile';
  if (player.linkState === 'existing_external') return 'Existing scouting profile';
  return 'Existing squad player';
};

const WyscoutClubRosterDialog: React.FC<Props> = ({open, workspaceId, playerId, onClose, onImported}) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();
  const pendingRef = useRef<object | null>(null);
  const currentRef = useRef({open, workspaceId, playerId});
  currentRef.current = {open, workspaceId, playerId};
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [months, setMonths] = useState<WyscoutImportMonths>(12);

  const roster = useQuery<WyscoutClubRosterPreview>({
    queryKey: playerKeys.wyscoutClubRoster(workspaceId, playerId),
    queryFn: ({signal}) => wyscoutClubRosterAPI.preview(workspaceId, playerId, signal),
    enabled: open,
    retry: false,
  });
  const preview: WyscoutClubRosterPreview | undefined = roster.data;

  const importRoster = useMutation({
    mutationFn: ({workspaceId: requestWorkspaceId, playerId: requestPlayerId, teamId, playerIds, months: selectedMonths}: ImportVariables) =>
      wyscoutClubRosterAPI.import(requestWorkspaceId, requestPlayerId, teamId, playerIds, selectedMonths),
    onSuccess: (result, variables) => {
      onImported(result, {
        workspaceId: variables.workspaceId,
        playerId: variables.playerId,
        existingPlayerIds: variables.existingPlayerIds,
      });
    },
    onSettled: (_result, _error, variables) => {
      if (pendingRef.current === variables.token) {
        pendingRef.current = null;
        setSubmitting(false);
        const current = currentRef.current;
        if (!current.open || current.workspaceId !== variables.workspaceId || current.playerId !== variables.playerId) {
          setMonths(12);
          const currentPreview = queryClient.getQueryData<WyscoutClubRosterPreview>(
            playerKeys.wyscoutClubRoster(current.workspaceId, current.playerId),
          );
          if (current.open && currentPreview) setSelected(new Set(currentPreview.players
            .filter(player => player.linkState === 'new')
            .slice(0, 100)
            .map(player => player.wyscoutPlayerId)));
        }
      }
    },
  });

  useEffect(() => {
    if (!pendingRef.current) {
      setSubmitting(false);
      importRoster.reset();
      setMonths(12);
    }
    if (!open) setSelected(new Set());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, playerId, workspaceId]);

  useEffect(() => {
    if (!open || !preview || pendingRef.current) return;
    setSelected(new Set(preview.players
      .filter(player => player.linkState === 'new')
      .slice(0, 100)
      .map(player => player.wyscoutPlayerId)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, playerId, preview, workspaceId]);

  useEffect(() => {
    if (open) return;
    void queryClient.cancelQueries({queryKey: playerKeys.wyscoutClubRoster(workspaceId, playerId), exact: true});
  }, [open, playerId, queryClient, workspaceId]);

  const eligible = preview?.players.filter(player => player.linkState !== 'existing_squad') ?? [];
  const selectedIds = eligible.filter(player => selected.has(player.wyscoutPlayerId)).map(player => player.wyscoutPlayerId);
  const count = selectedIds.length;
  const countLabel = `${count} ${count === 1 ? 'player' : 'players'}`;

  const requestClose = () => {
    if (!submitting) onClose();
  };

  const submit = () => {
    if (!preview || count < 1 || count > 100 || pendingRef.current) return;
    const token = {};
    const existingPlayerIds = Array.from(new Set(eligible
      .filter(player => selected.has(player.wyscoutPlayerId) && player.linkState === 'existing_external')
      .map(player => player.existingPlayerId)
      .filter((id): id is string => Boolean(id))));
    pendingRef.current = token;
    setSubmitting(true);
    importRoster.mutate({workspaceId, playerId, teamId: preview.team.wyscoutTeamId, playerIds: selectedIds, months, existingPlayerIds, token});
  };

  const refresh = () => {
    importRoster.reset();
    void roster.refetch();
  };

  const error = importRoster.isError ? getV2Error(importRoster.error) : null;
  const title = preview ? `${preview.team.name} club roster` : 'Club roster';

  return <Dialog open={open} onClose={requestClose} initialFocus={closeRef} className="relative z-50">
    <DialogBackdrop className="fixed inset-0 bg-black/60" />
    <div className="fixed inset-0 overflow-y-auto overflow-x-hidden p-0 sm:p-4">
      <div className="flex min-h-full items-end justify-center sm:items-center">
        <DialogPanel data-testid="wyscout-club-roster-panel" className="max-h-[96vh] w-full min-w-0 overflow-y-auto overflow-x-hidden rounded-t-2xl bg-white p-4 shadow-2xl dark:bg-gray-900 sm:max-w-4xl sm:rounded-2xl sm:p-6">
          <div className="flex min-w-0 items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="break-words text-xl font-bold text-gray-900 dark:text-gray-100">{title}</DialogTitle>
              {preview && <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{preview.players.length} current active {preview.players.length === 1 ? 'player' : 'players'}</p>}
            </div>
            <button ref={closeRef} type="button" aria-label="Close club roster" onClick={requestClose} className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md text-2xl text-gray-500 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:text-gray-300 dark:hover:bg-gray-800">×</button>
          </div>

          {roster.isPending && <LoadingStatus label="Loading club roster..." className="justify-center py-10" />}
          {roster.isError && <div role="alert" className="mt-5 rounded-lg bg-danger-50 p-4 text-sm text-danger-800 dark:bg-danger-950 dark:text-danger-200">
            <p>{getV2Error(roster.error).message}</p>
            <button type="button" onClick={() => { void roster.refetch(); }} className="mt-2 min-h-11 font-semibold underline">Try again</button>
          </div>}

          {preview && <>
            {preview.warnings.map(warning => <p key={warning} role="alert" className="mt-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950 dark:text-amber-200">{warning}</p>)}
            {preview.players.length === 0
              ? <p className="py-10 text-center text-gray-600 dark:text-gray-300">No current active players are available.</p>
              : <>
                <div className="mt-5 flex flex-wrap gap-2 border-y border-gray-200 py-3 dark:border-gray-700">
                  <button type="button" disabled={submitting} onClick={() => setSelected(new Set(eligible.slice(0, 100).map(player => player.wyscoutPlayerId)))} className="min-h-11 rounded-md px-3 font-semibold text-club-blue-700 underline disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:text-club-blue-300">Select all eligible</button>
                  <button type="button" disabled={submitting} onClick={() => setSelected(new Set())} className="min-h-11 rounded-md px-3 font-semibold text-gray-700 underline disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:text-gray-200">Clear selection</button>
                  <p className="self-center text-sm text-gray-600 dark:text-gray-300">Up to 100 players can be imported at once.</p>
                </div>
                <label className="mt-4 block text-sm font-semibold text-gray-800 dark:text-gray-200">
                  History to import
                  <select
                    value={months}
                    disabled={submitting}
                    onChange={event => setMonths(Number(event.target.value) as WyscoutImportMonths)}
                    className="mt-1 block min-h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                  >
                    <option value={12}>12 months</option>
                    <option value={24}>24 months</option>
                    <option value={36}>36 months</option>
                  </select>
                </label>
                <ul className="mt-3 space-y-2">
                  {preview.players.map(player => {
                    const squad = player.linkState === 'existing_squad';
                    const checked = selected.has(player.wyscoutPlayerId) && !squad;
                    const selectionFull = !checked && selected.size >= 100;
                    const details = [player.position, formatBirthDate(player.birthDate), player.nationality].filter(Boolean);
                    return <li key={player.wyscoutPlayerId} className="min-w-0 rounded-xl border border-gray-200 p-3 dark:border-gray-700">
                      <label className={`flex min-w-0 items-start gap-3 ${squad ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={squad || selectionFull || submitting}
                          onChange={event => setSelected(current => {
                            const next = new Set(current);
                            if (event.target.checked && next.size < 100) next.add(player.wyscoutPlayerId);
                            else next.delete(player.wyscoutPlayerId);
                            return next;
                          })}
                          aria-label={`${player.name}, ${stateLabel(player)}`}
                          className="mt-3 h-5 w-5 shrink-0 rounded border-gray-300 text-club-blue-600 focus:ring-club-blue-500 dark:border-gray-600 dark:bg-gray-800"
                        />
                        {player.photoUrl
                          ? <img src={player.photoUrl} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" />
                          : <span aria-hidden="true" className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-club-navy-950 font-bold text-white">{player.name.trim().charAt(0).toUpperCase() || '?'}</span>}
                        <span className="min-w-0 flex-1">
                          <span className="block break-words font-semibold text-gray-900 dark:text-gray-100">{player.name}</span>
                          {details.length > 0 && <span className="block break-words text-sm text-gray-600 dark:text-gray-300">{details.join(' | ')}</span>}
                          <span className="mt-1 block break-words text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{stateLabel(player)}</span>
                          {squad && <span className="mt-1 block break-words text-sm text-amber-800 dark:text-amber-200">Existing squad players cannot be imported or refreshed here.</span>}
                        </span>
                      </label>
                    </li>;
                  })}
                </ul>
              </>}
          </>}

          {error && <div role="alert" className="mt-4 rounded-lg bg-danger-50 p-3 text-sm text-danger-800 dark:bg-danger-950 dark:text-danger-200">
            <p>{error.message}</p>
            {error.code === 'stale_preview' || error.code === 'squad_conflict'
              ? <button type="button" onClick={refresh} className="mt-2 min-h-11 font-semibold underline">Refresh roster</button>
              : null}
          </div>}

          <div className="mt-5 flex flex-col-reverse gap-3 border-t border-gray-200 pt-4 dark:border-gray-700 sm:flex-row sm:justify-end">
            <button type="button" disabled={submitting} onClick={requestClose} className="min-h-11 rounded-md border border-gray-300 px-4 font-semibold text-gray-800 disabled:opacity-60 dark:border-gray-600 dark:text-gray-100">Cancel</button>
            <button type="button" disabled={!preview || count < 1 || count > 100 || submitting} onClick={submit} className="min-h-11 rounded-md bg-club-blue-700 px-4 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60">
              {submitting ? `Importing ${countLabel} and statistics` : `Import ${countLabel} and statistics`}
            </button>
          </div>
        </DialogPanel>
      </div>
    </div>
  </Dialog>;
};

export default WyscoutClubRosterDialog;
